import { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
} from "@mui/material";
import {
  OptionContainer,
  OptionRow,
  ActionRow,
  StyledFormControl,
  NoDataText,
} from "./Widget.styles";
import { NoDataContainer } from "../Dashboard.styles";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { fetchServiceModulesThunk, fetchLeaderboardActionModulesThunk, fetchLeaderboardDashboardThunk } from "../../../store/thunk/serviceModulesThunk";
import { useLeaderboardActionModules, useLeaderboardDashboardData, useServiceModules } from "../../../store/selectors/serviceModulesSelector";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const LeaderboardWidget = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const serviceModules = useServiceModules().filter((mod) => mod.type === "Leaderboard");
  const actionModules = useLeaderboardActionModules();
  const leaderboardData = useLeaderboardDashboardData();

  const [selectedServiceModuleId, setSelectedServiceModuleId] = useState<number | null>(null);
  const [selectedActionModuleId, setSelectedActionModuleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(true);

  const selectedServiceModule = useMemo(() => {
    return serviceModules.find((m) => m.id === selectedServiceModuleId);
  }, [serviceModules, selectedServiceModuleId]);

  useEffect(() => {
    dispatch(fetchServiceModulesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedServiceModule) {
      dispatch(fetchLeaderboardActionModulesThunk({
        isCompanyWideAssigned: selectedServiceModule.isCompanyWideAssigned,
        groupIds: selectedServiceModule.assignedGroupIds,
      }));
    }
    setSelectedActionModuleId(null);
  }, [dispatch, selectedServiceModule]);

  const fetchLeaderboardData = () => {
    if (!selectedServiceModuleId || !selectedActionModuleId) return;
    setLoading(true);
    dispatch(fetchLeaderboardDashboardThunk({
      serviceModuleId: selectedServiceModuleId,
      actionModuleId: selectedActionModuleId,
    }))
      .unwrap()
      .then((payload) => {
        setLoading(false);
        setNoData(payload.length === 0);
      })
      .catch((err) => {
        setLoading(false);
        setNoData(true);
      });
  };

  const resetState = () => {
    setSelectedServiceModuleId(null);
    setSelectedActionModuleId(null);
    setNoData(true);
  };

  return (
    <>
      <OptionContainer>
        <OptionRow>
          <StyledFormControl>
            <Select
              value={selectedServiceModuleId ?? ""}
              onChange={(e) => setSelectedServiceModuleId(Number(e.target.value))}
              displayEmpty
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="" disabled>
                {t("dashboardPage.leaderboardWidget.selectLeaderboard")}
              </MenuItem>
              {serviceModules.map((mod) => (
                <MenuItem key={mod.id} value={mod.id}>
                  <Box>{mod.name}</Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("dashboardPage.leaderboardWidget.leaderboardServiceModule")}</FormHelperText>
          </StyledFormControl>

          <StyledFormControl disabled={!selectedServiceModule}>
            <Select
              value={selectedActionModuleId ?? ""}
              onChange={(e) => setSelectedActionModuleId(Number(e.target.value))}
              displayEmpty
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="" disabled>
                {t("dashboardPage.leaderboardWidget.selectActionModule")}
              </MenuItem>
              {actionModules.map((mod) => (
                <MenuItem key={mod.id} value={mod.id}>
                  <Box>{mod.name}</Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("dashboardPage.leaderboardWidget.actionModule")}</FormHelperText>
          </StyledFormControl>

          <IconButton
            onClick={resetState}
            aria-label={t("buttons.reset")}
            sx={{ marginRight: 1, color: "#555" }}
            size="small"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </OptionRow>

        <ActionRow>
          <Tooltip
            title={
              !selectedServiceModuleId || !selectedActionModuleId
                ? t("dashboardPage.leaderboardWidget.actionModuleRequiredTooltip")
                : ""
            }
            disableHoverListener={!!(selectedServiceModuleId && selectedActionModuleId)}
          >
            <span>
              <GeneralButton
                appVariant="widget"
                onClick={fetchLeaderboardData}
                disabled={!selectedServiceModuleId || !selectedActionModuleId}
              >
                {t("buttons.apply")}
              </GeneralButton>
            </span>
          </Tooltip>
        </ActionRow>
      </OptionContainer>

      {loading || noData ? (
        <NoDataContainer>
          <NoDataText>{t("dashboardPage.leaderboardWidget.noLeaderboardData")}</NoDataText>
        </NoDataContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("dashboardPage.leaderboardWidget.rank")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.firstName")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.lastName")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.employeeNumber")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.position")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.status")}</TableCell>
                <TableCell>{t("dashboardPage.leaderboardWidget.score")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboardData.slice(0, 10).map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.rank}</TableCell>
                  <TableCell>{entry.firstName}</TableCell>
                  <TableCell>{entry.lastName}</TableCell>
                  <TableCell>{entry.employeeNumber}</TableCell>
                  <TableCell>{entry.position}</TableCell>
                  <TableCell>{entry.status}</TableCell>
                  <TableCell>{entry.score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default LeaderboardWidget;
